import { ChangeEvent, DragEvent, FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import {
	downloadAttachment,
	formatFileSize,
	getAttachmentCategory,
	getAttachmentDisplayName,
	MAX_ATTACHMENT_SIZE_BYTES,
	SUPPORTED_ATTACHMENT_ACCEPT,
} from '../attachmentUtils';
import { AttachmentUploadControlProps } from '../attachment.types';
import { CategoryFileIcon, DownloadActionIcon, RemoveActionIcon, UploadCloudIcon } from '../AttachmentIcons';
import styles from './AttachmentUploadControl.module.scss';

export const AttachmentUploadControl: FC<AttachmentUploadControlProps> = ({
	label,
	disabled = false,
	existingAttachments = [],
	stagedFiles = [],
	onAddFiles,
	onRemoveStagedFile,
	onRemoveExistingAttachment,
	className,
}) => {
	const { t } = useTranslation();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isDragActive, setIsDragActive] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const handleFiles = (incomingList: FileList | File[]) => {
		setErrorMessage(null);
		const filesArray = Array.from(incomingList);
		if (!filesArray.length) return;

		const oversizedFile = filesArray.find(file => file.size > MAX_ATTACHMENT_SIZE_BYTES);
		if (oversizedFile) {
			setErrorMessage(
				t('upload_tooLargeFile100', 'File size exceeds 100MB limit: {{name}}', {
					name: oversizedFile.name,
				})
			);
			return;
		}

		onAddFiles?.(filesArray);
	};

	const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		if (event.target.files) {
			handleFiles(event.target.files);
		}
		// Clear value so the same file can be re-selected if removed
		event.target.value = '';
	};

	const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (!disabled) setIsDragActive(true);
	};

	const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragActive(false);
	};

	const handleDrop = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragActive(false);
		if (disabled) return;

		if (e.dataTransfer?.files) {
			handleFiles(e.dataTransfer.files);
		}
	};

	const openBrowse = () => {
		if (!disabled) fileInputRef.current?.click();
	};

	return (
		<div className={cx(styles.container, className)}>
			<div className={styles.header}>
				<span className={styles.label}>{label || t('attachments' as any, 'Attachments')}</span>
			</div>

			<div
				className={cx(styles.dropzone, {
					[styles.dropzoneActive]: isDragActive,
					[styles.dropzoneDisabled]: disabled,
				})}
				onClick={openBrowse}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				role="button"
				tabIndex={disabled ? -1 : 0}
				aria-label="Upload files"
			>
				<UploadCloudIcon className={styles.cloudIcon} />
				<div className={styles.dropzoneText}>
					<span className={styles.primaryText}>
						{t('drag_files_or', 'Drag & drop files or')}
						<span className={styles.browseLink}>{t('browse', 'browse')}</span>
					</span>
					<span className={styles.hintText}>
						{t(
							'supported_attachments_hint',
							'PDF, DOC, DOCX, XLS, XLSX, CSV, TXT, Images, Videos up to 100MB'
						)}
					</span>
				</div>
				<input
					ref={fileInputRef}
					type="file"
					className={styles.hiddenInput}
					multiple
					accept={SUPPORTED_ATTACHMENT_ACCEPT}
					onChange={handleInputChange}
					disabled={disabled}
				/>
			</div>

			{errorMessage && <span className={styles.errorText}>{errorMessage}</span>}

			{(existingAttachments.length > 0 || stagedFiles.length > 0) && (
				<div className={styles.fileList}>
					{/* Existing S3 attachments */}
					{existingAttachments.map((key, index) => {
						const displayName = getAttachmentDisplayName(key);
						const category = getAttachmentCategory(key);

						return (
							<div key={`existing-${key}-${index}`} className={styles.fileCard}>
								<div className={styles.fileCard_left}>
									<div className={styles.fileCard_iconWrap}>
										<CategoryFileIcon category={category} />
									</div>
									<div className={styles.fileCard_meta}>
										<span className={styles.fileCard_name} title={displayName}>
											{displayName}
										</span>
										<span className={styles.fileCard_size}>
											{t('attached_file', 'Attached file')}
										</span>
									</div>
								</div>

								<div className={styles.fileCard_actions}>
									<button
										type="button"
										className={styles.fileCard_actionBtn}
										onClick={() => downloadAttachment(key, displayName)}
										title={t('download' as any, 'Download')}
										aria-label="Download attachment"
									>
										<DownloadActionIcon />
									</button>
									{!disabled && onRemoveExistingAttachment && (
										<button
											type="button"
											className={cx(styles.fileCard_actionBtn, styles.fileCard_actionBtnDanger)}
											onClick={() => onRemoveExistingAttachment(index)}
											title={t('delete' as any, 'Remove')}
											aria-label="Remove attachment"
										>
											<RemoveActionIcon />
										</button>
									)}
								</div>
							</div>
						);
					})}

					{/* Staged (pending upload) files */}
					{stagedFiles.map((file, index) => {
						const category = getAttachmentCategory(file);

						return (
							<div key={`staged-${file.name}-${index}`} className={styles.fileCard}>
								<div className={styles.fileCard_left}>
									<div className={styles.fileCard_iconWrap}>
										<CategoryFileIcon category={category} />
									</div>
									<div className={styles.fileCard_meta}>
										<span className={styles.fileCard_name} title={file.name}>
											{file.name}
										</span>
										<span className={styles.fileCard_size}>
											{formatFileSize(file.size)} •{' '}
											{t('ready_to_upload' as any, 'Ready to upload')}
										</span>
									</div>
								</div>

								{!disabled && onRemoveStagedFile && (
									<div className={styles.fileCard_actions}>
										<button
											type="button"
											className={cx(styles.fileCard_actionBtn, styles.fileCard_actionBtnDanger)}
											onClick={() => onRemoveStagedFile(index)}
											title={t('delete' as any, 'Remove')}
											aria-label="Remove staged file"
										>
											<RemoveActionIcon />
										</button>
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default AttachmentUploadControl;
